/**
 * Whether this browser has said the visitor is old enough (`age-floor`).
 *
 * A self-declaration, asked once at whichever door comes first and kept here
 * rather than on a server: a no never leaves the browser, and a yes reaches the
 * account only through the adapter that copies it. Nothing but the answer and
 * its moment is kept — no birth date was ever asked for.
 */

export const AGE_FLOOR = 16;

/** How long a no keeps every door shut before the question is asked again. */
export const REFUSAL_LASTS_MS = 30 * 24 * 60 * 60 * 1000;

const STORAGE_KEY = "syncani.age-floor";

export interface AgeAnswer {
  answer: "yes" | "no";
  /** ISO 8601. */
  at: string;
}

/** What the doors do: let the visitor on, refuse them, or ask. */
export type AgeStanding = "confirmed" | "refused" | "unasked";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** An answer, or null for anything this module did not write. */
export function parseAgeAnswer(raw: unknown): AgeAnswer | null {
  if (!isRecord(raw)) return null;
  const { answer, at } = raw;
  if (answer !== "yes" && answer !== "no") return null;
  if (typeof at !== "string" || Number.isNaN(Date.parse(at))) return null;
  return { answer, at };
}

export function ageStanding(
  answer: AgeAnswer | null,
  now: number,
): AgeStanding {
  if (!answer) return "unasked";
  if (answer.answer === "yes") return "confirmed";
  return now - Date.parse(answer.at) < REFUSAL_LASTS_MS ? "refused" : "unasked";
}

const listeners = new Set<() => void>();

export function subscribeAgeAnswer(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function readAgeAnswer(): AgeAnswer | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === null ? null : parseAgeAnswer(JSON.parse(stored));
  } catch {
    return null;
  }
}

export function rememberAgeAnswer(answer: AgeAnswer): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(answer));
  } catch {
    // A browser that refuses storage is asked again next time, which is the
    // safe side of forgetting.
  }
  for (const listener of listeners) listener();
}
